import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "@fmc-digital/i18n";
import { InfoModal } from "@profile/components/atoms/InfoModal";
import { WhileYouWaitChecklist } from "@profile/components/molecules/WhileYouWaitChecklist";
import { ResendSection } from "@profile/components/molecules/ResendSection";
import { useIsMinWidth768 } from "@profile/hooks/useIsMinWidth768";
import { useOtpExpiryTimer } from "@profile/hooks/useOtpExpiryTimer";
import { useScrollIntoViewIfNotVisible } from "@profile/hooks/useScrollIntoViewIfNotVisible";
import {
  cn,
  ErrorBanner,
  ErrorMessage,
  MFACodeInput,
  Button,
  type MFACodeInputRef,
} from "@fmc-digital/ui";
import { MFATimer } from "../../molecules/MFATimer";
import { getOtpExpirationMinutes } from "./mfa-code-container.constants";
import type { MFACodeContainerProps } from "./mfa-code-container.types";
import { useResendTimer } from "../../../hooks/useResendTimer";
import { SecurityBadge } from "@profile/components/registration-wizard/SecurityBadge";
import {
  registerOtpBodyStyles,
  registerOtpContentWrapperStyles,
  registerOtpDigitMobileStyles,
  registerOtpInputMobileStyles,
  registerOtpInputStyles,
  registerOtpStackStyles,
  registerSecurityBadgeStyles,
} from "@profile/styles/register-flow.styles";

export function MFACodeContainer(props: Readonly<MFACodeContainerProps>) {
  const { t } = useTranslation();
  const {
    factorState,
    flow = "registration",
    onVerify,
    onValidate,
    onResend,
    onCancel,
    inputError: externalInputError,
    onInputEdit,
    enableResendOnLoad,
    onConsumeEnableResendOnLoad,
    factorsList,
    onNavigateToFactorSelection,
    error,
    errorCode,
    onErrorClear,
    stateTokenExpiresAt,
    onStateTokenExpired,
    onTimerExpired,
  } = props;

  const isLoginFlow = flow === "login";
  const isRecoveryFlow = flow === "recovery";
  const isAuthFlow = isLoginFlow || isRecoveryFlow;
  const isInvalidCodeError =
    errorCode === "CUST_S1200004" || errorCode === "E0000068";
  const isAccountLocked =
    errorCode === "E0000069" ||
    errorCode === "E0000047" ||
    (error?.toLowerCase().includes("locked") ?? false);

  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const codeInputRef = useRef<MFACodeInputRef>(null);
  const lastValidationAttemptRef = useRef<string | null>(null);
  const validationRequestIdRef = useRef(0);
  const verificationCompleteRef = useRef(false);
  const errorBannerRef = useRef<HTMLDivElement>(null);
  const expiredBannerRef = useRef<HTMLDivElement>(null);
  const onValidateRef = useRef(onValidate);
  const onVerifyRef = useRef(onVerify);
  const factorStateRef = useRef(factorState);
  const externalInputErrorRef = useRef(externalInputError);
  const inlineAuthErrorRef = useRef<string | null>(null);

  onValidateRef.current = onValidate;
  onVerifyRef.current = onVerify;
  factorStateRef.current = factorState;
  externalInputErrorRef.current = externalInputError;

  const {
    minutes,
    seconds,
    isExpired,
    showExpiryModal,
    usesWallClockExpiry,
    resetTimer,
    resetExpiryTracking,
    dismissExpiryModal,
  } = useOtpExpiryTimer({
    flow,
    factorType: factorState.factorType,
    stateTokenExpiresAt,
    onTimerExpired,
  });

  const { resendWait, setResendWait } = useResendTimer(30);
  const [isResending, setIsResending] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const isResendDisabled = isResending || resendWait > 0;
  const isEmailFactor = factorState.factorType === "email";
  const isMdUp = useIsMinWidth768();

  const inlineAuthError = isAuthFlow && error && isInvalidCodeError ? error : null;
  inlineAuthErrorRef.current = inlineAuthError;
  const displayError = externalInputError ?? inputError ?? inlineAuthError;
  const showErrorBanner = isAuthFlow && !!error && !isInvalidCodeError;
  const showRecoveryExpiredBanner = isRecoveryFlow && isExpired && !isLoginFlow;
  const showInlineExpiredBanner =
    isExpired && !showRecoveryExpiredBanner && !isLoginFlow;

  useScrollIntoViewIfNotVisible(errorBannerRef, showErrorBanner);
  useScrollIntoViewIfNotVisible(expiredBannerRef, showRecoveryExpiredBanner);

  useEffect(() => {
    if (!enableResendOnLoad) return;
    setResendWait(0);
    onConsumeEnableResendOnLoad?.();
  }, [enableResendOnLoad, onConsumeEnableResendOnLoad, setResendWait]);

  const handleResend = useCallback(async () => {
    setIsResending(true);

    try {
      onErrorClear?.();
      await onResend?.();

      setCode(["", "", "", "", "", ""]);
      setInputError(null);
      setIsSuccess(false);
      verificationCompleteRef.current = false;
      lastValidationAttemptRef.current = null;
      onInputEdit?.();

      if (isLoginFlow || usesWallClockExpiry) {
        resetExpiryTracking();
      } else {
        resetTimer(getOtpExpirationMinutes(factorState.factorType));
      }

      setResendWait(30);

      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 0);
    } catch {
      // Parent handler sets user-facing error state.
    } finally {
      setIsResending(false);
    }
  }, [
    factorState.factorType,
    isLoginFlow,
    onErrorClear,
    onInputEdit,
    onResend,
    resetExpiryTracking,
    resetTimer,
    setResendWait,
    usesWallClockExpiry,
  ]);

  const handleSendToAlternate = () => {
    const selectedFactorType = factorState.factorType ?? "email";
    const targetFactorType = selectedFactorType === "sms" ? "email" : "sms";

    if (factorsList && factorsList.length > 0) {
      const alternateFactor = factorsList.find((factor) => {
        const factorTypeValue = factor.factorType?.toLowerCase() ?? "";
        if (targetFactorType === "email") {
          return factorTypeValue.includes("email");
        }
        return factorTypeValue.includes("sms");
      });

      if (alternateFactor) {
        onNavigateToFactorSelection?.(targetFactorType);
      }
    } else {
      onNavigateToFactorSelection?.(targetFactorType);
    }
  };

  const hasAlternateFactor = () => {
    if (!onNavigateToFactorSelection || !factorsList || factorsList.length <= 1) {
      return false;
    }

    const selectedFactorType = factorState.factorType ?? "email";
    const targetFactorType = selectedFactorType === "sms" ? "email" : "sms";
    return factorsList.some((factor) => {
      const factorTypeValue = factor.factorType?.toLowerCase() ?? "";
      if (targetFactorType === "email") {
        return factorTypeValue.includes("email");
      }
      return factorTypeValue.includes("sms");
    });
  };

  const getAlternateOptionText = () => {
    if (factorState.factorType === "sms") {
      return t("auth.mfa.sendToEmail");
    }
    return t("auth.mfa.sendToPhone");
  };

  const handleStateTokenExpiredConfirm = () => {
    dismissExpiryModal();
    onStateTokenExpired?.();
  };

  const handleCodeChange = (val: string[]) => {
    setInputError(null);
    setIsSuccess(false);
    verificationCompleteRef.current = false;
    lastValidationAttemptRef.current = null;
    setCode(val);
    onInputEdit?.();
    if (error && onErrorClear) {
      onErrorClear();
    }
  };

  const isCodeComplete = code.length === 6 && code.every((digit) => digit !== "");
  const completeCode = isCodeComplete ? code.join("") : null;

  useEffect(() => {
    if (!onValidateRef.current || !completeCode || isExpired || isAccountLocked) return;
    if (lastValidationAttemptRef.current === completeCode) return;

    let didCancel = false;
    const codeToValidate = completeCode;
    const requestId = ++validationRequestIdRef.current;
    lastValidationAttemptRef.current = codeToValidate;

    const resolveValidationErrorMessage = () =>
      externalInputErrorRef.current
      ?? inlineAuthErrorRef.current
      ?? "The code you entered is invalid";

    const validate = async () => {
      setIsValidating(true);
      try {
        const response = await onValidateRef.current?.(codeToValidate);
        if (didCancel || requestId !== validationRequestIdRef.current) return;
        const isValidationSuccess = response?.status === "SUCCESS" || response?.success === true;
        if (isValidationSuccess) {
          setIsSuccess(true);
          setInputError(null);
          verificationCompleteRef.current = true;
          onVerifyRef.current?.(codeToValidate, factorStateRef.current);
        } else if (response?.status === "BANNER_ONLY") {
          setInputError(null);
        } else if (response?.status === "CLEAR_CODE") {
          setInputError(null);
          setIsSuccess(false);
          setCode(["", "", "", "", "", ""]);
          lastValidationAttemptRef.current = null;
        } else if (response?.status === "ALREADY_VALIDATED") {
          setInputError(null);
          verificationCompleteRef.current = true;
          onVerifyRef.current?.(codeToValidate, factorStateRef.current);
        } else if (response == null) {
          setInputError(null);
        } else {
          setInputError(resolveValidationErrorMessage());
        }
      } catch {
        if (didCancel || requestId !== validationRequestIdRef.current) return;
        setInputError(resolveValidationErrorMessage());
      } finally {
        if (!didCancel && requestId === validationRequestIdRef.current) {
          setIsValidating(false);
        }
      }
    };
    validate();
    return () => {
      didCancel = true;
    };
  }, [completeCode, isAccountLocked, isExpired]);

  const passcodeExpiredTitle = t(
    "auth.login.passcodeExpiredTitle",
    "Passcode Expired",
  );
  const passcodeExpiredBody = t(
    "auth.login.passcodeExpiredBody",
    "Your login token has expired. Please log in again.",
  );

  const expiredBannerMessage = isRecoveryFlow
    ? t("errors.codeExpired")
    : t("auth.error.codeExpired");

  return (
    <div
      className={cn(registerOtpBodyStyles())}
      data-testid="mfa-code-container"
    >
      <div className={cn("mfa-code-content-wrapper", registerOtpContentWrapperStyles())}>
        <div className={cn("mfa-otp-stack", registerOtpStackStyles())}>
          <MFACodeInput
            ref={codeInputRef}
            code={code}
            onChange={handleCodeChange}
            error={!!displayError || !!error}
            disabled={isSuccess || isExpired || isValidating || isAccountLocked}
            success={isSuccess}
            className={cn(
              registerOtpInputStyles(),
              !isMdUp && registerOtpInputMobileStyles(),
            )}
            digitClassName={isMdUp ? undefined : cn(registerOtpDigitMobileStyles())}
          />
          {displayError && (
            <div className="mfa-invalid-code-error-wrap">
              <ErrorMessage
                className="mfa-invalid-code-error"
                data-testid="mfa-invalid-code-error"
              >
                {displayError}
              </ErrorMessage>
            </div>
          )}
        </div>
        {!isExpired && (
          <div className="mt-4">
            <MFATimer minutes={minutes} seconds={seconds} />
          </div>
        )}
        <div className="mt-4 text-left">
          <ResendSection
            resendWait={isExpired ? 0 : resendWait}
            isResendDisabled={isResendDisabled}
            onResend={handleResend}
          />
        </div>
      </div>

      {showInlineExpiredBanner && (
        <ErrorBanner
          message={expiredBannerMessage}
          className="mt-4 [&_span]:text-[14px] [&_span]:leading-5"
          testID="mfa-code-expired-banner"
        />
      )}

      {isEmailFactor && (
        <div className="mt-4">
          <WhileYouWaitChecklist />
        </div>
      )}

      {onCancel && (
        <div className="mt-4 flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 mfa-code-buttons-wrapper">
          <Button
            variant="outlined"
            onClick={onCancel}
            className="w-full lg:flex-1"
            disabled={isExpired}
            data-testid="mfa-cancel-button"
          >
            {t("common.cancel")}
          </Button>
        </div>
      )}

      {hasAlternateFactor() && (
        <div className="text-center mfa-alternate-factor-link mt-4">
          <button
            type="button"
            onClick={handleSendToAlternate}
            className="mfa-resend-link"
          >
            {getAlternateOptionText()}
          </button>
        </div>
      )}

      {isAuthFlow && (
        <div className={cn(registerSecurityBadgeStyles())}>
          <SecurityBadge />
        </div>
      )}

      {showErrorBanner && (
        <div ref={errorBannerRef}>
          <ErrorBanner message={error} className="mt-6 md:mt-8 mb-8" />
        </div>
      )}

      {showRecoveryExpiredBanner && (
        <div ref={expiredBannerRef}>
          <ErrorBanner
            message={t("errors.codeExpired")}
            className="mt-6 md:mt-8 mb-8"
          />
        </div>
      )}

      {isLoginFlow && (
        <InfoModal
          open={showExpiryModal}
          onClose={handleStateTokenExpiredConfirm}
          title={passcodeExpiredTitle}
          body={passcodeExpiredBody}
          variant="loginSessionExpiry"
          hideSecondaryAction
          showCloseButton={false}
          closeOnOverlayClick={false}
          closeOnEscape={false}
          primaryAction={{
            label: t("auth.login.passcodeExpiredLogin", "Login"),
            onClick: handleStateTokenExpiredConfirm,
          }}
        />
      )}
    </div>
  );
}

/** @deprecated Use MFACodeContainer */
export const MFACodeContainerV2 = MFACodeContainer;
